import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 360;
const MARGIN = { top: 30, right: 30, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

const PRIOR_COLOR = '#9ca3af';
const LIKELIHOOD_COLOR = '#f59e0b';
const POSTERIOR_COLOR = '#2563eb';

export default function BayesianUpdateChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.prior || !data.likelihood || !data.posterior) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const { prior, likelihood, posterior, credibleInterval, shrinkage } = data;

    // Collect all x and y values to determine domains
    const allPoints = [
      ...(prior.points || []),
      ...(likelihood.points || []),
      ...(posterior.points || []),
    ];

    if (allPoints.length === 0) return;

    const xExtent = d3.extent(allPoints, (d) => d.x);
    const xPadding = (xExtent[1] - xExtent[0]) * 0.05 || 1;
    const yMax = d3.max(allPoints, (d) => d.y);

    // Scales
    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPadding, xExtent[1] + xPadding])
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([0, yMax * 1.15])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(10)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('Price ($)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => v.toFixed(2))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('Probability Density');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Area and line generators
    const areaGen = d3.area()
      .curve(d3.curveBasis)
      .x((d) => x(d.x))
      .y0(innerH)
      .y1((d) => y(d.y));

    const lineGen = d3.line()
      .curve(d3.curveBasis)
      .x((d) => x(d.x))
      .y((d) => y(d.y));

    // -- Credible interval shaded region (draw first, behind everything) --
    if (credibleInterval && credibleInterval.low != null && credibleInterval.high != null) {
      const ciPoints = posterior.points.filter(
        (p) => p.x >= credibleInterval.low && p.x <= credibleInterval.high
      );

      if (ciPoints.length > 0) {
        // Add boundary points for clean edges
        const ciWithEdges = [
          { x: credibleInterval.low, y: 0 },
          ...ciPoints,
          { x: credibleInterval.high, y: 0 },
        ];

        const ciArea = d3.area()
          .curve(d3.curveBasis)
          .x((d) => x(d.x))
          .y0(innerH)
          .y1((d) => y(d.y));

        g.append('path')
          .datum(ciWithEdges)
          .attr('d', ciArea)
          .attr('fill', POSTERIOR_COLOR)
          .attr('fill-opacity', 0.12)
          .attr('stroke', 'none');

        // Credible interval boundary lines
        [credibleInterval.low, credibleInterval.high].forEach((val) => {
          g.append('line')
            .attr('x1', x(val))
            .attr('x2', x(val))
            .attr('y1', 0)
            .attr('y2', innerH)
            .attr('stroke', POSTERIOR_COLOR)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '2,3')
            .attr('stroke-opacity', 0.4);
        });
      }
    }

    // -- Prior: gray dashed area and line --
    g.append('path')
      .datum(prior.points)
      .attr('d', areaGen)
      .attr('fill', PRIOR_COLOR)
      .attr('fill-opacity', 0.1)
      .attr('stroke', 'none');

    g.append('path')
      .datum(prior.points)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', PRIOR_COLOR)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,4');

    // -- Likelihood: orange area and line --
    g.append('path')
      .datum(likelihood.points)
      .attr('d', areaGen)
      .attr('fill', LIKELIHOOD_COLOR)
      .attr('fill-opacity', 0.12)
      .attr('stroke', 'none');

    g.append('path')
      .datum(likelihood.points)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', LIKELIHOOD_COLOR)
      .attr('stroke-width', 2);

    // -- Posterior: blue solid area and line --
    g.append('path')
      .datum(posterior.points)
      .attr('d', areaGen)
      .attr('fill', POSTERIOR_COLOR)
      .attr('fill-opacity', 0.15)
      .attr('stroke', 'none');

    g.append('path')
      .datum(posterior.points)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', POSTERIOR_COLOR)
      .attr('stroke-width', 2.5);

    // -- Vertical mean lines for prior and posterior --
    // Prior mean
    if (prior.mean != null) {
      g.append('line')
        .attr('x1', x(prior.mean))
        .attr('x2', x(prior.mean))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', PRIOR_COLOR)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3');

      g.append('text')
        .attr('x', x(prior.mean))
        .attr('y', -6)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', PRIOR_COLOR)
        .text(`Prior \u03BC=$${prior.mean.toFixed(2)}`);
    }

    // Posterior mean
    if (posterior.mean != null) {
      g.append('line')
        .attr('x1', x(posterior.mean))
        .attr('x2', x(posterior.mean))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', POSTERIOR_COLOR)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3');

      g.append('text')
        .attr('x', x(posterior.mean))
        .attr('y', -6)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', POSTERIOR_COLOR)
        .text(`Post. \u03BC=$${posterior.mean.toFixed(2)}`);
    }

    // -- Annotation box: shrinkage + credible interval (top-right) --
    const boxWidth = 180;
    const boxHeight = credibleInterval ? 60 : 32;
    const boxX = innerW - boxWidth - 8;
    const boxY = 8;

    const annoG = g.append('g').attr('transform', `translate(${boxX}, ${boxY})`);

    annoG.append('rect')
      .attr('width', boxWidth)
      .attr('height', boxHeight)
      .attr('rx', 6)
      .attr('fill', 'rgba(255, 255, 255, 0.92)')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1);

    if (shrinkage != null) {
      const pct = (shrinkage * 100).toFixed(1);
      annoG.append('text')
        .attr('x', 10)
        .attr('y', 18)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(`Shrinkage: ${pct}%`);
    }

    if (credibleInterval) {
      const level = credibleInterval.level != null
        ? `${(credibleInterval.level * 100).toFixed(0)}%`
        : '95%';

      annoG.append('text')
        .attr('x', 10)
        .attr('y', 36)
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(`${level} Credible Interval:`);

      annoG.append('text')
        .attr('x', 10)
        .attr('y', 52)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', POSTERIOR_COLOR)
        .text(`$${credibleInterval.low.toFixed(2)} \u2013 $${credibleInterval.high.toFixed(2)}`);
    }

    // -- Invisible overlay for tooltip on hover --
    const overlay = g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const xVal = x.invert(mx);

      // Find closest point in each distribution
      const findClosest = (points) => {
        if (!points || points.length === 0) return null;
        let closest = points[0];
        let minDist = Math.abs(points[0].x - xVal);
        for (let i = 1; i < points.length; i++) {
          const dist = Math.abs(points[i].x - xVal);
          if (dist < minDist) {
            minDist = dist;
            closest = points[i];
          }
        }
        return closest;
      };

      const priorPt = findClosest(prior.points);
      const likelihoodPt = findClosest(likelihood.points);
      const posteriorPt = findClosest(posterior.points);

      setTooltip({
        x: event.clientX + 12,
        y: event.clientY - 10,
        price: xVal,
        priorDensity: priorPt ? priorPt.y : 0,
        likelihoodDensity: likelihoodPt ? likelihoodPt.y : 0,
        posteriorDensity: posteriorPt ? posteriorPt.y : 0,
      });
    });

    overlay.on('mouseleave', () => {
      setTooltip(null);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.prior || !data.likelihood || !data.posterior) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Bayesian Price Update</div>
          <div style={styles.subtitle}>How new scrape data updates price expectations</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No Bayesian update data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Bayesian Price Update</div>
        <div style={styles.subtitle}>How new scrape data updates price expectations</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={PRIOR_COLOR} strokeWidth="2" strokeDasharray="4,3" />
          </svg>
          <span>{data.prior.label || 'Prior'}</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={LIKELIHOOD_COLOR} strokeWidth="2" />
          </svg>
          <span>{data.likelihood.label || 'Likelihood'}</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={POSTERIOR_COLOR} strokeWidth="2.5" />
          </svg>
          <span>{data.posterior.label || 'Posterior'}</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill={POSTERIOR_COLOR} fillOpacity="0.12" rx="2" />
          </svg>
          <span>
            {data.credibleInterval
              ? `${((data.credibleInterval.level || 0.95) * 100).toFixed(0)}% Credible Interval`
              : '95% Credible Interval'}
          </span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            Price: ${tooltip.price.toFixed(2)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: PRIOR_COLOR }}>Prior</span>
            <span style={{ fontWeight: 600, color: PRIOR_COLOR }}>
              {tooltip.priorDensity.toFixed(4)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: LIKELIHOOD_COLOR }}>Likelihood</span>
            <span style={{ fontWeight: 600, color: LIKELIHOOD_COLOR }}>
              {tooltip.likelihoodDensity.toFixed(4)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: POSTERIOR_COLOR }}>Posterior</span>
            <span style={{ fontWeight: 600, color: POSTERIOR_COLOR }}>
              {tooltip.posteriorDensity.toFixed(4)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
